/**
 * The parent print of a child's week or month of orders (PROD-1064): the
 * range math, the `/orders/family/print` URL contract, and the page models
 * the print components render.
 *
 * Pure (no React, no fetches) so the rules are unit-tested directly:
 *
 * - A WEEK is Monday to Friday of the chosen week.
 * - A MONTH is one month from the start date the parent picks (default: the
 *   first of next month): Nov 1 → Nov 30, Oct 26 → Nov 25.
 * - Only PLACED meals print: cart lines, cancelled and refunded lines, and
 *   payment-pending drafts are left out, so the paper never promises a meal
 *   that may not come.
 * - A day with meals prints them even if the school later switched the day
 *   off (same rule as the week grid's day card); otherwise a no-service day
 *   reads "No service" and a served day with nothing ordered "Not ordered".
 */

import type { FamilyOrderWire } from '@/api/family-orders'
import { type Mealtime, MEALTIME_LABELS, sortMealtimes } from '@/lib/mealtime'
import { addDaysKey, dateFromKey, dateKey, isWeekday, mondayOf } from '@/lib/school-orders/calendar'

export type FamilyPrintRange = 'week' | 'month'

export interface FamilyPrintRequest {
  range: FamilyPrintRange
  /** First day printed, `YYYY-MM-DD`. */
  from: string
  /** Last day printed (inclusive), `YYYY-MM-DD`. */
  to: string
  /** One student's user id, or null for every student in the household. */
  studentId: string | null
}

/** Mirrors the server's `FAMILY_ORDERS_MAX_RANGE_DAYS`: the longest range one request may ask for. */
export const FAMILY_PRINT_MAX_RANGE_DAYS = 62

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const DAY_MS = 24 * 60 * 60 * 1000

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const LONG_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function parts(key: string): { year: number; month: number; day: number } {
  const date = dateFromKey(key)
  return { year: date.getUTCFullYear(), month: date.getUTCMonth(), day: date.getUTCDate() }
}

function daysBetween(from: string, to: string): number {
  return Math.round((dateFromKey(to).getTime() - dateFromKey(from).getTime()) / DAY_MS)
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()
}

// ---- Ranges --------------------------------------------------------------

/** Monday to Friday of the week holding `key` (a weekend key snaps forward, like the week grid). */
export function weekPrintRange(key: string): { from: string; to: string } {
  const monday = mondayOf(key)
  return { from: monday, to: addDaysKey(monday, 4) }
}

/**
 * One month from `start`: the day before the same date next month. A start
 * past the end of next month clamps to that month's last day first, so
 * Jan 31 → Feb 27 (2026), never into March.
 */
export function monthPrintRange(start: string): { from: string; to: string } {
  const { year, month, day } = parts(start)
  const nextYear = month === 11 ? year + 1 : year
  const nextMonth = (month + 1) % 12
  const sameDayNextMonth = dateKey(nextYear, nextMonth + 1, Math.min(day, daysInMonth(nextYear, nextMonth)))
  return { from: start, to: addDaysKey(sameDayNextMonth, -1) }
}

/** The first of the month after `today`: where a month print starts by default. */
export function firstOfNextMonth(today: string): string {
  const { year, month } = parts(today)
  return month === 11 ? dateKey(year + 1, 1, 1) : dateKey(year, month + 2, 1)
}

/** True when `from`–`to` is a range one print may ask for. */
export function isPrintableRange(from: string, to: string): boolean {
  if (!ISO_DAY.test(from) || !ISO_DAY.test(to)) {
    return false
  }
  const span = daysBetween(from, to) + 1
  return span >= 1 && span <= FAMILY_PRINT_MAX_RANGE_DAYS
}

// ---- URL contract --------------------------------------------------------

export const FAMILY_PRINT_PATH = '/orders/family/print'

export function familyPrintHref(request: FamilyPrintRequest): string {
  const params = new URLSearchParams({ range: request.range, from: request.from, to: request.to })
  if (request.studentId) {
    params.set('student', request.studentId)
  }
  return `${FAMILY_PRINT_PATH}?${params.toString()}`
}

/** The print page's request, or null when the link is missing or malformed. */
export function parseFamilyPrintRequest(params: URLSearchParams): FamilyPrintRequest | null {
  const range = params.get('range')
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''
  if ((range !== 'week' && range !== 'month') || !isPrintableRange(from, to)) {
    return null
  }
  return { range, from, to, studentId: params.get('student') || null }
}

// ---- Labels --------------------------------------------------------------

/**
 * "Oct 26 – 30", "Oct 26 – Nov 25", or one day "Nov 2". With `withYear` the
 * year follows ("Nov 1 – 30, 2026"); a range across New Year names both.
 * `longMonth` spells the months out ("November 2 – 6") where the range is
 * the thing being read, like the Print dialog's selection.
 */
export function formatPrintRange(
  from: string,
  to: string,
  options: { withYear?: boolean; longMonth?: boolean } = {},
): string {
  const months = options.longMonth ? LONG_MONTHS : SHORT_MONTHS
  const a = parts(from)
  const b = parts(to)
  const startLabel = `${months[a.month]} ${a.day}`
  if (options.withYear && a.year !== b.year) {
    return `${startLabel}, ${a.year} – ${months[b.month]} ${b.day}, ${b.year}`
  }
  const year = options.withYear ? `, ${b.year}` : ''
  if (from === to) {
    return `${startLabel}${year}`
  }
  const endLabel = a.month === b.month ? String(b.day) : `${months[b.month]} ${b.day}`
  return `${startLabel} – ${endLabel}${year}`
}

/**
 * The period in a printed page's title: a whole calendar month reads as its
 * name ("October"), any other range as its days ("October 26 – 30").
 */
export function printPeriodTitle(from: string, to: string): string {
  const a = parts(from)
  const b = parts(to)
  const wholeMonth = a.day === 1 && a.year === b.year && a.month === b.month && b.day === daysInMonth(b.year, b.month)
  return wholeMonth ? (LONG_MONTHS[a.month] ?? '') : formatPrintRange(from, to, { longMonth: true })
}

/** The page's subtitle lead: "Breakfast and lunch", "Lunch", "Breakfast, lunch and snack". */
export function printMealtimesPhrase(mealtimes: readonly Mealtime[]): string {
  const labels = mealtimes.map((mealtime, i) =>
    i === 0 ? MEALTIME_LABELS[mealtime] : MEALTIME_LABELS[mealtime].toLowerCase(),
  )
  if (labels.length <= 1) {
    return labels[0] ?? ''
  }
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/** "Grade 3" for a numeric grade; anything else (TK, K, PK) as the school wrote it. */
export function printGradeLabel(gradeLevel: string | null | undefined): string | null {
  const grade = gradeLevel?.trim()
  if (!grade) {
    return null
  }
  return /^\d+$/.test(grade) ? `Grade ${Number(grade)}` : grade
}

// ---- Meals ---------------------------------------------------------------

export interface PrintMeal {
  /** The first part of the meal's name: what the child looks for. */
  main: string
  /** Everything after it ("Baked Beans · Applesauce · Milk"); empty for a single item. */
  sides: string
  quantity: number
}

/** "Beef Hot Dog • Baked Beans • Milk" → main "Beef Hot Dog", sides "Baked Beans · Milk". */
export function splitMealTitle(title: string): { main: string; sides: string } {
  const [main = '', ...rest] = title
    .split('•')
    .map((part) => part.trim())
    .filter(Boolean)
  return { main, sides: rest.join(' · ') }
}

/** Placed, live meals for one student: `dateKey → mealtime → meals`. */
export type StudentMeals = Map<string, Map<Mealtime, PrintMeal[]>>

/** Whether a projected line is a meal that is still coming. */
function isPlacedLine(order: FamilyOrderWire, item: FamilyOrderWire['items'][number]): boolean {
  const orderLive = (order.state ?? 'active') === 'active'
  const lineLive = (item.lineState ?? 'active') === 'active'
  return orderLive && lineLive && item.submitted && item.quantity > 0
}

export function studentMealsByDay(orders: readonly FamilyOrderWire[], studentUserId: string): StudentMeals {
  const byDay: StudentMeals = new Map()
  for (const order of orders) {
    const day = order.serviceDate.slice(0, 10)
    for (const item of order.items) {
      if (item.studentUserId !== studentUserId || !isPlacedLine(order, item)) {
        continue
      }
      const byMealtime = byDay.get(day) ?? new Map<Mealtime, PrintMeal[]>()
      const meals = byMealtime.get(order.mealtime) ?? []
      const { main, sides } = splitMealTitle(item.title)
      const same = meals.find((meal) => meal.main === main && meal.sides === sides)
      if (same) {
        same.quantity += item.quantity
      } else {
        meals.push({ main, sides, quantity: item.quantity })
      }
      byMealtime.set(order.mealtime, meals)
      byDay.set(day, byMealtime)
    }
  }
  return byDay
}

/** Days in `from`–`to` on which any of `studentUserIds` has a placed meal. */
export function countDaysWithOrders(
  orders: readonly FamilyOrderWire[],
  studentUserIds: ReadonlySet<string>,
  from: string,
  to: string,
): number {
  const days = new Set<string>()
  for (const order of orders) {
    const day = order.serviceDate.slice(0, 10)
    if (day < from || day > to) {
      continue
    }
    if (order.items.some((item) => studentUserIds.has(item.studentUserId ?? '') && isPlacedLine(order, item))) {
      days.add(day)
    }
  }
  return days.size
}

/** The meal columns a print shows: every mealtime any printed student has a meal in, in time-of-day order. */
export function printMealtimes(meals: readonly StudentMeals[]): Mealtime[] {
  const found = new Set<Mealtime>()
  for (const student of meals) {
    for (const byMealtime of student.values()) {
      for (const mealtime of byMealtime.keys()) {
        found.add(mealtime)
      }
    }
  }
  return found.size > 0 ? sortMealtimes(found) : ['BREAKFAST', 'LUNCH']
}

// ---- Page models ---------------------------------------------------------

export type PrintDayState = 'meals' | 'no-service' | 'not-ordered'

export interface PrintDay {
  dateKey: string
  /** "Monday" */
  weekday: string
  /** "October 26" */
  dateLabel: string
  /** Day of the month, for the month grid. */
  dayOfMonth: number
  /** "Oct" */
  monthShort: string
  state: PrintDayState
  /** One list per printed mealtime, in the same order. */
  meals: PrintMeal[][]
}

interface DayInput {
  mealtimes: readonly Mealtime[]
  meals: StudentMeals
  /** The school serves no meals that day (blocked date, day off, out of session). */
  isNoService: (dateKey: string) => boolean
}

function printDay(key: string, input: DayInput): PrintDay {
  const { month, day } = parts(key)
  const byMealtime = input.meals.get(key)
  const meals = input.mealtimes.map((mealtime) => byMealtime?.get(mealtime) ?? [])
  const hasMeals = meals.some((list) => list.length > 0)
  return {
    dateKey: key,
    weekday: WEEKDAYS[dateFromKey(key).getUTCDay()] ?? '',
    dateLabel: `${LONG_MONTHS[month]} ${day}`,
    dayOfMonth: day,
    monthShort: SHORT_MONTHS[month] ?? '',
    state: hasMeals ? 'meals' : input.isNoService(key) ? 'no-service' : 'not-ordered',
    meals,
  }
}

/** The week page's rows: each weekday from `from` to `to`. */
export function buildWeekDays(input: DayInput & { from: string; to: string }): PrintDay[] {
  const days: PrintDay[] = []
  for (let key = input.from; key <= input.to; key = addDaysKey(key, 1)) {
    if (isWeekday(key)) {
      days.push(printDay(key, input))
    }
  }
  return days
}

export type MonthCell =
  | { kind: 'outside'; dateKey: string }
  /** `showMonth`: the first date the grid shows, and the first of each new month, carry the month ("Nov 2"). */
  | ({ kind: 'day'; showMonth: boolean } & PrintDay)

export type MonthRow =
  /** A week whose every printed day has no service collapses to one slim line. */
  { kind: 'closed'; label: string } | { kind: 'week'; cells: MonthCell[] }

/**
 * The month grid: Monday-to-Friday rows from the week holding `from` to the
 * week holding `to`. Days outside the range stay as blank cells so the
 * columns line up; a week with no printed weekday is skipped.
 */
export function buildMonthRows(input: DayInput & { from: string; to: string }): MonthRow[] {
  const rows: MonthRow[] = []
  let monthShown: string | null = null
  for (let monday = mondayOf(input.from); monday <= input.to; monday = addDaysKey(monday, 7)) {
    const cells: MonthCell[] = []
    for (let offset = 0; offset < 5; offset += 1) {
      const key = addDaysKey(monday, offset)
      cells.push(
        key < input.from || key > input.to
          ? { kind: 'outside', dateKey: key }
          : { kind: 'day', showMonth: false, ...printDay(key, input) },
      )
    }
    const printed = cells.filter((cell): cell is Extract<MonthCell, { kind: 'day' }> => cell.kind === 'day')
    if (printed.length === 0) {
      continue
    }
    if (printed.every((cell) => cell.state === 'no-service')) {
      rows.push({
        kind: 'closed',
        label: formatPrintRange(printed[0]!.dateKey, printed[printed.length - 1]!.dateKey),
      })
    } else {
      for (const cell of printed) {
        cell.showMonth = cell.monthShort !== monthShown
        monthShown = cell.monthShort
      }
      rows.push({ kind: 'week', cells })
    }
  }
  return rows
}

/** Every Monday whose week overlaps `from`–`to`: the menu weeks to ask "is the school serving?". */
export function printWeekStarts(from: string, to: string): string[] {
  // `mondayOf` snaps a weekend forward, and the week before a weekend `from`
  // has no weekday in range, so starting there never misses a week.
  const mondays: string[] = []
  for (let monday = mondayOf(from); monday <= to; monday = addDaysKey(monday, 7)) {
    mondays.push(monday)
  }
  return mondays
}
